import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import loadDynamicImage from '../Entities/ImageLoader';
import SummaryPanel from '../Entities/SummaryPanel';
import PlantDetailsModal from './Plants/PlantDetailsModal';

import { add_notifications } from '../../api/api_notify';
import { fetchSpacesForUser } from '../../api/api_users';
import { fetchHubsForSpace } from '../../api/api_spaces';
import { fetchLastMeasureForHub } from '../../api/api_hubs';
import { getPlantOfTheDay } from '../../api/api_plants';
import '../styles/General.css';
import '../styles/Devices.css';
import '../styles/Home.css';
import '../styles/IrrigationScheme.css';
import '../styles/Login.css';
import '../styles/Pots.css';
import '../styles/Plants.css';
import '../styles/Users.css';
import '../styles/FastActions.css';
import '../styles/PotManager.css';
import '../styles/PotDetails.css';
import no_warn_gif from '../assets/no_warn.gif';
import warn_gif from '../assets/warn.gif';
import warn_low_gif from '../assets/warn_low.gif';

function Home() {
    const { translations } = useLanguage();
    const navigate = useNavigate();
    const [spaces, setSpaces] = useState([]);
    const [plantOfTheDay, setPlantOfTheDay] = useState('');
    const [error, setError] = useState('');
    const [showPlantModal, setShowPlantModal] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('userToken');
        add_notifications(token);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const plant_name = await getPlantOfTheDay();
                setPlantOfTheDay(plant_name);
            } catch (error) {}
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchSpacesAndHubs = async () => {
            try {
                const token = localStorage.getItem('userToken');
                const response = await fetchSpacesForUser(token);

                const spacesData = await Promise.all(response.data.map(async (space) => {
                    const hubs = await fetchHubsForSpace(space.id, token);
                    const hubsWithIrrigation = hubs.data.filter(hub => hub.irrigation_system_enabled);

                    const hubsWithLastMeasure = await Promise.all(hubsWithIrrigation.map(async (hub) => {
                        const lastMeasure = await fetchLastMeasureForHub(hub.id, token);
                        return { ...hub, lastMeasure: lastMeasure.data };
                    }));

                    return { ...space, hubs: hubsWithLastMeasure };
                }));

                const spacesWithIrrigation = spacesData.filter(space => space.hubs.length > 0);

                setSpaces(spacesWithIrrigation);

            } catch (error) {
                if (error.response && error.response.status === 403) {
                    setError(translations.unauthorizedError);
                } else if (error.response && error.response.status === 404) {
                    setError(translations.spacesNotFoundError);
                } else {
                    setError(translations.fetchDataError, error);
                }
            }
        };
        fetchSpacesAndHubs();
    }, [translations]);


    const CustomPrevArrow = ({ onClick }) => (
        <button
            className="custom-arrow custom-prev-arrow"
            onClick={onClick}
        >
            &#9664;
        </button>
    );

    const CustomNextArrow = ({ onClick }) => (
        <button
            className="custom-arrow custom-next-arrow"
            onClick={onClick}
        >
            &#9654;
        </button>
    );

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: false,
        autoplaySpeed: 5000,
        pauseOnHover: true,
        prevNextButtons: true,
        adaptiveHeight: false,
        arrows: true,
        prevArrow: <CustomPrevArrow />,
        nextArrow: <CustomNextArrow />,
    };

    const alerts = [
        translations.redPotAlert,
        translations.blackPotAlert,
        translations.bedroomHubAlert
    ];

    const showWaterLevel = spaces.some(space => space.hubs.some(hub => hub.irrigation_system_enabled));

    const handleNavigate = (path) => {
        navigate(path);
    };

    const handlePlantModalOpen = () => setShowPlantModal(true);

    return (
        <div className="general-page">
            {showPlantModal && (
                <PlantDetailsModal
                    plantName={plantOfTheDay}
                    onClose={() => setShowPlantModal(false)}
                    translations={translations}
                />
            )}
            <div className="plant-of-the-day-button" onClick={handlePlantModalOpen}>
                {plantOfTheDay && loadDynamicImage(plantOfTheDay) ? (
                    <img src={loadDynamicImage(plantOfTheDay)} alt="Plant of the Day" />
                ) : (
                    <div className="no-plant">{translations.noPlantOfTheDay}</div>
                )}
            </div>
            <div className="container">
                <Slider {...settings}>
                    <div className="gallery-cell">
                        {alerts.length > 0 ? (
                            <>
                                <div className="alertsContainer">
                                    <div className="container-header">
                                        <h2>{translations.alerts}</h2>
                                        {alerts.map((alert, index) => (
                                            <div key={index} className="alert">{alert}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="low-alert-container">
                                    <div className="container-header">
                                        <h2>{translations.alerts_low}</h2>
                                        {alerts.map((alert, index) => (
                                            <div key={index} className="alert low-alert">{alert}</div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="no-alert-container">
                                <div className="container-header">
                                    <p>{translations.no_alerts}</p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="gallery-cell">
                        <SummaryPanel spaces={spaces} showWaterLevel={showWaterLevel} translations={translations} error={error} />
                    </div>
                </Slider>
                <div className="prior">
                    <button onClick={() => handleNavigate('/pots/potManager')}>{translations.pots}</button>
                    <button onClick={() => handleNavigate('/fastActions')}>{translations.fastActions}</button>
                    <button onClick={() => handleNavigate('/spaces/spaceManager')}>{translations.configuration}</button>
                </div>
            </div>
        </div>
    );
}

export default Home;
